import { Fragment } from 'react';

import { useGMetrixTable } from 'components/GMetrixTable/context';
import { useResponsiveDropdownMenuConfig } from 'hooks';
import { Dropdown, Form } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';

export type TableFilterOption = {
    checked: boolean;
    label: string;
    onSelect: (checked: boolean) => void;
    value?: string | number;
}

type TableFilterBase = {
    label: string;
    type: 'checkbox' | 'radio';
}

// A singular filter owns its own checked + onSelect; a grouped filter is a
// heading plus a list of self-contained options (checked / onSelect per option).
export type TableFilter =
    | (TableFilterBase & { checked: boolean; onSelect: (checked: boolean) => void; options?: never })
    | (TableFilterBase & { checked?: never; onSelect?: never; options: TableFilterOption[] });

export const GMetrixTableFilters = () => {

    const { filters, uniqueTableId } = useGMetrixTable();

    const popperConfig = useResponsiveDropdownMenuConfig('md');

    const handleSelect = (filter: TableFilter, option?: TableFilterOption) => {

        const target = option ?? filter;

        if (filter.type === 'radio' && target.checked) return;

        target.onSelect?.(option && filter.type === 'checkbox' ? !option.checked : !target.checked);

    };

    return (
        <Dropdown autoClose={'outside'} className={'gmetrix-table-filters d-flex'}>
            <Dropdown.Toggle className={'d-inline-flex align-items-center gap-1 flex-fill justify-content-between'} variant={'outline-secondary'}>
                <div className={'d-flex align-items-center gap-1'}>
                    <i className={'fa-regular fa-filter'} />
                    <span>
                        <FormattedMessage defaultMessage="Filters" id="filters.906751ae" />
                    </span>
                </div>
            </Dropdown.Toggle>
            <Dropdown.Menu renderOnMount popperConfig={popperConfig}>
                {filters?.map((entry, entryIndex) => (
                    <Fragment key={entryIndex}>
                        {!entry.options ?
                            (
                                <Dropdown.Item
                                    as={'div'}
                                    className={'d-flex gap-2 text-nowrap'}
                                    onClick={() => handleSelect(entry)}
                                    role="button"
                                >
                                    <Form.Check
                                        checked={entry.checked}
                                        id={`filter_${uniqueTableId}_${entryIndex}`}
                                        type={entry.type}
                                    />
                                    <Form.Label className={'text-wrap m-0'} htmlFor={`filter_${uniqueTableId}_${entryIndex}`}>
                                        {entry.label}
                                    </Form.Label>
                                </Dropdown.Item>
                            )
                            :
                            (
                                <>
                                    <Dropdown.Header>
                                        {entry.label}
                                    </Dropdown.Header>
                                    {entry.options.map((option, optionIndex) => (
                                        <Dropdown.Item
                                            as={'div'}
                                            className={'d-flex gap-2 text-nowrap'}
                                            key={`${entryIndex}-${optionIndex}`}
                                            onClick={() => handleSelect(entry, option)}
                                            role="button"
                                        >
                                            <Form.Check
                                                checked={option.checked}
                                                id={`filter_${uniqueTableId}_${entryIndex}_${optionIndex}`}
                                                name={`filter_group_${uniqueTableId}_${entryIndex}`}
                                                type={entry.type}
                                            />
                                            <Form.Label className={'text-wrap m-0'} htmlFor={`filter_${uniqueTableId}_${entryIndex}_${optionIndex}`}>
                                                {option.label}
                                            </Form.Label>
                                        </Dropdown.Item>
                                    ))}
                                </>
                            )
                        }
                        {entryIndex !== filters.length - 1 && (!!entry.options || !!filters[entryIndex + 1].options) && <Dropdown.Divider />}
                    </Fragment>
                ))}
            </Dropdown.Menu>
        </Dropdown>
    );

};
